import { z } from 'zod'
import { BOOKING_KINDS, BOOKING_TYPES } from './constants'

// A YYYY-MM-DD date and an HH:MM time, as the booking form collects them.
// The API resolves the pair against Europe/Helsinki -- every time a member
// reads or types is Finnish wall-clock time.
export const DateStringSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
// Also accepts the bare four digits the legacy form used (1430): members
// have typed it that way for twenty years.
export const TimeStringSchema = z
  .string()
  .regex(/^(\d{4}|\d{1,2}[:.]\d{2})$/, 'Expected HH:MM')

export const BookingKindSchema = z.enum(BOOKING_KINDS)
export const BookingTypeSchema = z.enum(BOOKING_TYPES)

// Flight-time estimates are entered with a comma by Finnish members
// ("1,5"); the legacy form did the same substitution server-side. Hours and
// minutes ("0:45", "1:30", "00:45") are read as the pilot means them.
export const FlightHoursSchema = z.union([
  z.number(),
  z.string().transform((v, ctx) => {
    const clock = /^\s*(\d{1,2}):([0-5]\d)\s*$/.exec(v)
    const parsed = clock ? Number(clock[1]) + Number(clock[2]) / 60 : Number(v.replace(',', '.'))
    if (!Number.isFinite(parsed)) {
      ctx.addIssue({ code: 'custom', message: 'Expected hours as a decimal (1,5) or as hours and minutes (1:30)' })
      return z.NEVER
    }
    return parsed
  }),
])

// The aircraft is identified by its registration everywhere, which is both
// its primary key in flight.aircraft and what a member actually says out
// loud. A booking request reads as "OH-CAM, Tuesday, 06:30".
export const RegistrationSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(3)
  .max(10)

export const CreateBookingSchema = z.object({
  registration: RegistrationSchema,
  startDate: DateStringSchema,
  startTime: TimeStringSchema,
  endDate: DateStringSchema,
  endTime: TimeStringSchema,
  estimatedFlightHours: FlightHoursSchema,
  comment: z.string().trim().max(255).nullable().optional(),
  kind: BookingKindSchema.default('reservation'),
  // Left out, it is 'training' when a lesson is named and 'private'
  // otherwise. 'maintenance' needs flight.maintenance.write.
  bookingType: BookingTypeSchema.optional(),
  // A standby only: drop it if it has not been confirmed this many hours
  // before the booking starts (0 = at the start). Absent: it waits until the
  // booking has passed or the member cancels it.
  dropHoursBeforeStart: z.number().int().min(0).max(168).nullable().optional(),
  // Booking on behalf of another member. Requires flight.booking.manage_any
  // -- the legacy form was a free-text field prefilled with your own login,
  // so anyone could book as anyone.
  userId: z.string().optional(),
  // A flight school lesson: the student's enrolment, optionally the
  // syllabus lesson, and the instructor flying it. See
  // db/flyway/schema/sql/V290__bookings.sql.
  enrolmentId: z.number().int().positive().nullable().optional(),
  lessonId: z.number().int().positive().nullable().optional(),
  instructorUserId: z.string().min(1).nullable().optional(),
})
export type CreateBookingInput = z.infer<typeof CreateBookingSchema>

export const UpdateBookingSchema = z.object({
  startDate: DateStringSchema,
  startTime: TimeStringSchema,
  endDate: DateStringSchema,
  endTime: TimeStringSchema,
  estimatedFlightHours: FlightHoursSchema,
  comment: z.string().trim().max(255).nullable().optional(),
  // A training booking's lesson and instructor can be changed; absent
  // leaves them as they are, null clears the lesson.
  lessonId: z.number().int().positive().nullable().optional(),
  instructorUserId: z.string().min(1).nullable().optional(),
})
export type UpdateBookingInput = z.infer<typeof UpdateBookingSchema>

// Hand a reservation to another member. They are told by email; there is
// nothing for them to accept.
export const TransferBookingSchema = z.object({
  toUserId: z.string().min(1),
})
export type TransferBookingInput = z.infer<typeof TransferBookingSchema>

// A dispatcher releasing fire patrol bookings. The reason is optional: the
// owner of an ordinary booking cancelled for them is told it, a dispatcher's
// own patrol bookings are not an apology.
export const ReleaseFirePatrolSchema = z.object({
  reason: z.string().trim().max(255).optional(),
})

export const CancelBookingSchema = z.object({
  // Required when cancelling someone else's booking; the legacy admin
  // cancel form defaulted it to "ei ilmoitettu".
  reason: z.string().trim().max(255).optional(),
})

// An administrator cancelling many bookings at once: everything still ahead
// for a member, for an aircraft (optionally within a date range), or both.
// `preview` lists what would be cancelled without cancelling it, so the
// confirmation dialog can show the administrator exactly that list.
export const CancelManyBookingsSchema = z
  .object({
    userId: z.string().min(1).optional(),
    registration: RegistrationSchema.optional(),
    fromDate: DateStringSchema.optional(),
    toDate: DateStringSchema.optional(),
    reason: z.string().trim().min(1).max(255).optional(),
    preview: z.boolean().optional(),
  })
  .refine((v) => v.userId || v.registration, { message: 'Choose a member or an aircraft' })
  .refine((v) => v.preview || v.reason, { message: 'A reason is required', path: ['reason'] })
export type CancelManyBookingsInput = z.infer<typeof CancelManyBookingsSchema>

// Repeats one daily window across a date range -- the `Palolennot`
// (fire-patrol) pattern, which the legacy code implemented as a loop inside
// ProcessReservation.php capped at 180 iterations. Days that clash become
// options rather than failing the batch.
export const BulkBookingSchema = z.object({
  registration: RegistrationSchema,
  fromDate: DateStringSchema,
  toDate: DateStringSchema,
  startTime: TimeStringSchema,
  endTime: TimeStringSchema,
  estimatedFlightHours: FlightHoursSchema,
  comment: z.string().trim().max(255).nullable().optional(),
  userId: z.string().optional(),
})
export type BulkBookingInput = z.infer<typeof BulkBookingSchema>

export const BookingQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  registration: RegistrationSchema.optional(),
  kind: BookingKindSchema.optional(),
  bookingType: BookingTypeSchema.optional(),
  includeCancelled: z.coerce.boolean().optional(),
})

// Why a booking was refused. The API returns these as a `violations` array
// alongside the problem detail so the UI can explain each one in the
// member's own language rather than echoing a server-side sentence.
export const RULE_VIOLATION_CODES = [
  'end_before_start',
  'starts_in_past',
  'flight_time_invalid',
  'flight_time_exceeds_duration',
  'too_long',
  'too_far_ahead',
  'aircraft_unavailable',
  'aircraft_aog',
  'aircraft_maintenance',
  'overlaps_existing',
  'instructor_busy',
  'instructor_unavailable',
  'training_needs_enrolment',
  'lesson_on_non_training',
  'maintenance_standby',
  'maintenance_not_permitted',
  'transfer_not_reservation',
  'transfer_not_private',
  'transfer_to_self',
  'transfer_fire_patrol',
  'transfer_finished',
  'transfer_recipient_not_permitted',
  'standby_drop_on_reservation',
  'standby_drop_in_past',
  'cancel_finished',
  'edit_finished',
  'standby_needs_clash',
  'standby_on_own_booking',
  'maintenance_booking_not_permitted',
  'member_not_active',
  'cannot_make_reservations',
] as const
export type RuleViolationCode = (typeof RULE_VIOLATION_CODES)[number]
